import { Captions, ClipboardList, ListChecks, MessageSquareText, ScrollText, type LucideIcon } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/misc";
import { missedBadge } from "@/lib/phone-page";
import { DEFAULT_PANES, readPanes, writePanes, type PaneChoice } from "@/lib/phone-work";
import { cn } from "@/lib/utils";
import { RAIL_CELL } from "@/softphone/rail";
import { PHONE_COMPANION } from "@/softphone/voicemail-suggestion";

/**
 * The Phone page's panes that a rep switches on and off: the form, the intake, the script, the
 * lead's texts (ACL-561) and the call's live transcript.
 *
 * On a campaign call the page is the call's card and, beside it, whichever of the two the rep
 * wants: both side by side, one of them, or the card by itself. The switches are two buttons in
 * the phone's rail (`PaneRailButtons`), under the day's calls, because the rail is where a rep
 * already goes to bring something up; a narrow window has no rail, so there the same two are
 * buttons in the page's strip (`PaneStripButtons`). The page draws what the choice says
 * (`usePanes`), and says which of the two it has a place for right now (`useOfferPanes`): a
 * button is there only then, since a switch that changes nothing on screen reads as broken.
 *
 * The choice is this browser's (`readPanes` in `lib/phone-work.ts`), and a module store rather
 * than the page's state because the page and the rail are in different parts of the tree, and the
 * rail is also drawn where there is no router (`/dev/phone`), as `softphone/stage.ts` says of
 * itself.
 */

/** Read once, the first time somebody asks, so storage is not touched while modules load. */
let choice: PaneChoice | null = null;
/** How many pages have a place for each pane right now: the Phone page, on a campaign's call or
 *  lead. Counts, not flags, so a page going and another coming do not undo each other. */
const offered: Record<keyof PaneChoice, number> = { form: 0, script: 0, intake: 0, texts: 0, transcript: 0 };
/** What the page has to say on a pane's switch: the texts nobody has opened yet. */
const counts: Record<keyof PaneChoice, number> = { form: 0, script: 0, intake: 0, texts: 0, transcript: 0 };
const NAMES = ["intake", "form", "script", "transcript", "texts"] as const;
const watchers = new Set<() => void>();

const subscribe = (notify: () => void) => {
  watchers.add(notify);
  return () => {
    watchers.delete(notify);
  };
};

function changed(): void {
  for (const notify of watchers) notify();
}

function current(): PaneChoice {
  choice ??= readPanes();
  return choice;
}

/** Which panes the rep has switched on. */
export function usePanes(): PaneChoice {
  return React.useSyncExternalStore(subscribe, current, () => DEFAULT_PANES);
}

/**
 * The panes that cannot share the page, in groups: the intake's questions and the form, and the
 * script, the lead's texts and the call's live transcript.
 *
 * The intake and the form are both a sheet of fields the rep works down, and side by side they are
 * two columns of inputs that look alike and are not — the intake's answers route the flow, the
 * form's do not. Turning one on therefore puts the other away. The script, the texts and the
 * transcript are read rather than filled, and any of them belongs beside whichever sheet is up;
 * together they would make the page three or four columns beside the card, so they take turns
 * too (ACL-561): one column to read, with the sheet's full height for whichever is up.
 */
const EXCLUSIVE: readonly (readonly (keyof PaneChoice)[])[] = [["intake", "form"], ["script", "texts", "transcript"]];

/** Puts away the other panes of `name`'s group, now that `name` is on. */
function putAwayPartner(next: PaneChoice, name: keyof PaneChoice): void {
  for (const pair of EXCLUSIVE) {
    if (pair.includes(name)) for (const other of pair) if (other !== name) next[other] = false;
  }
}

/** Switches one pane on or off, and remembers it. */
export function togglePane(name: keyof PaneChoice): void {
  const now = current();
  const next = { ...now, [name]: !now[name] };
  // Switching one of a pair ON puts the other away. Switching it off takes nothing with it: a
  // rep who wants the call's card by itself asks for that by turning both off.
  if (next[name]) putAwayPartner(next, name);
  choice = next;
  writePanes(choice);
  changed();
}

/**
 * Puts one pane on the page because the page needs it there, not because the rep asked: a call that
 * connects shows the form (ACL-573), Send contract on the call shows it where it marks what to fill
 * in (ACL-560), and a text the lead sends during the call shows the texts (ACL-561). The pair's other
 * pane goes away as it would for a press, but nothing is remembered, so the next visit opens as the
 * rep left it.
 */
export function showPane(name: keyof PaneChoice): void {
  const now = current();
  if (now[name]) return;
  const next = { ...now, [name]: true };
  putAwayPartner(next, name);
  choice = next;
  changed();
}

/** The page says which panes it has a place for, for as long as each holds. */
export function useOfferPanes({ form, script, intake, texts, transcript }: PaneChoice): void {
  React.useEffect(() => {
    const has: PaneChoice = { form, script, intake, texts, transcript };
    const mine = NAMES.filter((name) => has[name]);
    if (mine.length === 0) return;
    for (const name of mine) offered[name] += 1;
    changed();
    return () => {
      for (const name of mine) offered[name] = Math.max(0, offered[name] - 1);
      changed();
    };
  }, [form, script, intake, texts, transcript]);
}

/** Whether a page on screen has a place for this pane. */
function useOffered(name: keyof PaneChoice): boolean {
  return React.useSyncExternalStore(subscribe, () => offered[name] > 0, () => false);
}

/** The page says how many things wait in a pane (the texts nobody has opened), for its switch to
 *  carry, for as long as it holds. */
export function usePaneCount(name: keyof PaneChoice, count: number): void {
  React.useEffect(() => {
    if (count <= 0) return;
    counts[name] = count;
    changed();
    return () => {
      counts[name] = 0;
      changed();
    };
  }, [name, count]);
}

/** What a pane's switch carries: a count, or null for nothing. */
function usePaneBadge(name: keyof PaneChoice): string | null {
  return React.useSyncExternalStore(subscribe, () => missedBadge(counts[name]), () => null);
}

/** Forgets what was read, so the next reader asks storage again. For tests. */
export function resetPanes(): void {
  choice = null;
  changed();
}

//: The rail's order, top first. The intake leads because on a call it is the work: it is what a
//: rep is reading while they talk, and it is on by default, so it is the switch they reach for.
//: The transcript is next to the script, whose place it takes.
const PANES: { name: keyof PaneChoice; label: string; the: string; icon: LucideIcon }[] = [
  { name: "intake", label: "Intake", the: "the intake questions", icon: ListChecks },
  { name: "form", label: "Form", the: "the form", icon: ClipboardList },
  { name: "script", label: "Script", the: "the script", icon: ScrollText },
  { name: "transcript", label: "Transcript", the: "the live transcript", icon: Captions },
  { name: "texts", label: "Texts", the: "the lead's texts", icon: MessageSquareText },
];

/**
 * The switches in the phone's rail, under the day's calls, each while the Phone page has a
 * place for its pane. A pane that is on has its button held down, in the accent tone; the tooltip
 * says what a press does. They carry the companion mark, so the phone's own popover does not take a press on
 * them for a click somewhere else on the page and close itself.
 */
export function PaneRailButtons() {
  const on = usePanes();
  const there = {
    form: useOffered("form"), script: useOffered("script"), intake: useOffered("intake"), texts: useOffered("texts"),
    transcript: useOffered("transcript"),
  };
  const badge: Partial<Record<keyof PaneChoice, string | null>> = { texts: usePaneBadge("texts") };
  return (
    <>
      {PANES.filter(({ name }) => there[name]).map(({ name, label, the, icon: Icon }) => (
        <div
          key={name}
          className={RAIL_CELL}
          {...{ [PHONE_COMPANION]: "" }}
        >
          <Tooltip content={on[name] ? `Hide ${the}` : `Show ${the}`} side="left">
            <button
              type="button"
              aria-label={badge[name] ? `${label}, ${badge[name]} unread` : label}
              aria-pressed={on[name]}
              onClick={() => togglePane(name)}
              className={cn(
                "relative flex size-9 items-center justify-center rounded-full transition-colors",
                on[name]
                  ? "bg-accent-3 text-accent-11 hover:bg-accent-4"
                  : "text-text-muted hover:bg-frame-hover hover:text-text",
              )}
            >
              <Icon className="size-[18px]" aria-hidden />
              {badge[name] && (
                // On the button's corner, as the missed calls' count is (`missed-calls.tsx`).
                <span
                  className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-9 px-1 text-2xs font-semibold leading-none text-white ring-2 ring-frame tabular"
                  aria-hidden
                >
                  {badge[name]}
                </span>
              )}
            </button>
          </Tooltip>
        </div>
      ))}
    </>
  );
}

/** The same switches with their words on them, for the Phone page's strip where a narrow
 *  window has no rail. */
export function PaneStripButtons() {
  const on = usePanes();
  const there = {
    form: useOffered("form"), script: useOffered("script"), intake: useOffered("intake"), texts: useOffered("texts"),
    transcript: useOffered("transcript"),
  };
  const badge: Partial<Record<keyof PaneChoice, string | null>> = { texts: usePaneBadge("texts") };
  return (
    <>
      {PANES.filter(({ name }) => there[name]).map(({ name, label, icon: Icon }) => (
        <Button
          key={name}
          variant="outline"
          aria-pressed={on[name]}
          className={cn(on[name] && "border-accent-6 bg-accent-3 text-accent-11 hover:bg-accent-4")}
          onClick={() => togglePane(name)}
        >
          <Icon />
          {label}
          {badge[name] && (
            <Badge size="sm" tone="danger" aria-label={`${badge[name]} unread`}>
              {badge[name]}
            </Badge>
          )}
        </Button>
      ))}
    </>
  );
}
